import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { useAdminFeedback, FeedbackType, FeedbackStatus, FeedbackEntry } from '@/hooks/useAdminFeedback';
import { useAdminNotifications } from '@/hooks/useAdminNotifications';
import { useAIUsageStats } from '@/hooks/useAIUsageStats';
import { usePremiumAuditLogs } from '@/hooks/usePremiumAuditLogs';
import { useAdminUsers, AdminUser } from '@/hooks/useAdminUsers';
import { useAdminUserActivity } from '@/hooks/useAdminUserActivity';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ArrowLeft, RefreshCw, AlertCircle, Bug, Lightbulb, MessageSquare, ArrowUpDown, ArrowUp, ArrowDown, Bell, Check, X, Brain, Zap, Trash2, Eye, CreditCard, Users, Crown, Search, ChevronLeft, ChevronRight, Shield, Mail, CheckCircle, Dumbbell, Apple, Scale, Award, Clock, Activity, Download, Send, Smartphone } from 'lucide-react';
import { AdminPushPanel } from '@/components/AdminPushPanel';
import { PlatformDebugPanel } from '@/components/PlatformDebugPanel';
import { format, formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  reviewed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  addressed: 'bg-green-500/20 text-green-400 border-green-500/30',
  dismissed: 'bg-muted text-muted-foreground border-muted',
};

const typeIcons: Record<string, React.ReactNode> = {
  bug: <Bug className="h-4 w-4 text-red-400" />,
  feature: <Lightbulb className="h-4 w-4 text-yellow-400" />,
  general: <MessageSquare className="h-4 w-4 text-blue-400" />,
};

export default function Admin() {
  const navigate = useNavigate();
  const { isAdmin, loading: authLoading } = useAdminAuth();
  const {
    feedback,
    loading,
    error,
    filterType,
    setFilterType,
    filterStatus,
    setFilterStatus,
    sortField,
    setSortField,
    sortOrder,
    setSortOrder,
    updateStatus,
    deleteFeedback,
    refetch,
  } = useAdminFeedback();

  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackEntry | null>(null);
  
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useAdminNotifications();

  const {
    topUsers: aiTopUsers,
    totalRequests: aiTotalRequests,
    totalTokens: aiTotalTokens,
    loading: aiLoading,
    error: aiError,
    refetch: aiRefetch,
  } = useAIUsageStats();

  const {
    logs: premiumLogs,
    loading: premiumLogsLoading,
    error: premiumLogsError,
    refetch: premiumLogsRefetch,
  } = usePremiumAuditLogs();

  const {
    users,
    loading: usersLoading,
    error: usersError,
    totalUsers,
    currentPage,
    totalPages,
    fetchUsers,
    togglePremium,
    exportToCsv,
  } = useAdminUsers();

  const {
    activity,
    loading: activityLoading,
    error: activityError,
    fetchActivity,
    clearActivity,
  } = useAdminUserActivity();

  const [activeTab, setActiveTab] = useState('feedback');
  const [userSearch, setUserSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [viewingActivityUser, setViewingActivityUser] = useState<AdminUser | null>(null);
  const [activityTab, setActivityTab] = useState('workouts');
  const [premiumReason, setPremiumReason] = useState('');
  const [cancelStripeSubscription, setCancelStripeSubscription] = useState(true);
  const [isToggling, setIsToggling] = useState(false);
  const [showCreateTestUser, setShowCreateTestUser] = useState(false);
  const [testUserEmail, setTestUserEmail] = useState('');
  const [testUserPassword, setTestUserPassword] = useState('');
  const [testUserName, setTestUserName] = useState('');
  const [isCreatingTestUser, setIsCreatingTestUser] = useState(false);

  // Fetch users when switching to users tab
  useEffect(() => {
    if (activeTab === 'users' && users.length === 0) {
      fetchUsers(1, '');
    }
  }, [activeTab, users.length, fetchUsers]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 p-4">
        <AlertCircle className="h-16 w-16 text-destructive" />
        <h1 className="text-2xl font-bold text-foreground">Access Denied</h1>
        <p className="text-muted-foreground text-center">You don't have permission to view this page.</p>
        <Button onClick={() => navigate('/')} variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  const handleStatusChange = async (id: string, status: FeedbackStatus) => {
    const success = await updateStatus(id, status);
    if (success) {
      toast.success('Status updated');
    } else {
      toast.error('Failed to update status');
    }
  };

  const toggleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const handleDelete = async (id: string) => {
    const success = await deleteFeedback(id);
    if (success) {
      toast.success('Feedback deleted');
    } else {
      toast.error('Failed to delete feedback');
    }
  };

  const handleUserSearch = () => {
    fetchUsers(1, userSearch);
  };

  const handlePageChange = (page: number) => {
    fetchUsers(page, userSearch);
  };

  const handleTogglePremium = async (user: AdminUser) => {
    setIsToggling(true);
    const newStatus = !user.is_premium;
    const result = await togglePremium(
      user.id, 
      newStatus, 
      premiumReason || undefined,
      !newStatus ? cancelStripeSubscription : undefined // Only pass when revoking
    );
    
    if (result.success) {
      const stripeCancelledMsg = result.data?.stripeCancelled ? ' (Stripe subscription cancelled)' : '';
      toast.success(`${newStatus ? 'Granted' : 'Revoked'} premium for ${user.name || user.email}${stripeCancelledMsg}`);
      setSelectedUser(null);
      setPremiumReason('');
      setCancelStripeSubscription(true);
    } else {
      toast.error(result.error || 'Failed to toggle premium');
    }
    setIsToggling(false);
  };

  const handleExportCsv = async () => {
    const result = await exportToCsv();
    if (result.success) {
      toast.success('Users exported to CSV');
    } else {
      toast.error(result.error || 'Failed to export');
    }
  };

  const handleViewActivity = async (user: AdminUser) => {
    setViewingActivityUser(user);
    setActivityTab('workouts');
    await fetchActivity(user.id);
  };

  const handleCloseActivity = () => {
    setViewingActivityUser(null);
    clearActivity();
  };

  const handleCreateTestUser = async () => {
    if (!testUserEmail || !testUserPassword) {
      toast.error('Email and password are required');
      return;
    }

    setIsCreatingTestUser(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-create-test-user', {
        body: {
          email: testUserEmail,
          password: testUserPassword,
          name: testUserName || 'Test User',
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success(`Test account created: ${testUserEmail}`);
      setShowCreateTestUser(false);
      setTestUserEmail('');
      setTestUserPassword('');
      setTestUserName('');
      fetchUsers(1, '');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create test user');
    } finally {
      setIsCreatingTestUser(false);
    }
  };

  const SortIcon = ({ field }: { field: typeof sortField }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 opacity-50" />;
    return sortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto p-4 max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold text-foreground">Admin Dashboard</h1>
          </div>
          <div className="flex items-center gap-2">
            {/* Tab Switcher */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mr-2">
              <TabsList>
                <TabsTrigger value="feedback" className="flex items-center gap-1.5">
                  <MessageSquare className="h-4 w-4" />
                  Feedback
                </TabsTrigger>
                <TabsTrigger value="users" className="flex items-center gap-1.5">
                  <Users className="h-4 w-4" />
                  Users
                </TabsTrigger>
                <TabsTrigger value="push" className="flex items-center gap-1.5">
                  <Send className="h-4 w-4" />
                  Push
                </TabsTrigger>
                <TabsTrigger value="ai-usage" className="flex items-center gap-1.5">
                  <Brain className="h-4 w-4" />
                  AI Usage
                </TabsTrigger>
                <TabsTrigger value="premium-audit" className="flex items-center gap-1.5">
                  <CreditCard className="h-4 w-4" />
                  Premium
                </TabsTrigger>
                <TabsTrigger value="debug" className="flex items-center gap-1.5">
                  <Smartphone className="h-4 w-4" />
                  Debug
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Notifications Bell */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="icon" className="relative">
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary text-xs font-bold flex items-center justify-center text-primary-foreground">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="end">
                <div className="flex items-center justify-between p-3 border-b border-border">
                  <h3 className="font-semibold">Notifications</h3>
                  {unreadCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={markAllAsRead}>
                      Mark all read
                    </Button>
                  )}
                </div>
                <ScrollArea className="h-[300px]">
                  {notifications.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground text-sm">
                      No notifications
                    </div>
                  ) : (
                    <div className="divide-y divide-border">
                      {notifications.map((notification) => (
                        <div
                          key={notification.id}
                          className={`p-3 hover:bg-muted/50 transition-colors ${
                            !notification.is_read ? 'bg-primary/5' : ''
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm">{notification.title}</p>
                              <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                                {notification.message}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {notification.metadata?.user_name && (
                                  <span className="font-medium">{notification.metadata.user_name} • </span>
                                )}
                                {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                              </p>
                            </div>
                            <div className="flex items-center gap-1">
                              {!notification.is_read && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={() => markAsRead(notification.id)}
                                >
                                  <Check className="h-3 w-3" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-muted-foreground hover:text-destructive"
                                onClick={() => deleteNotification(notification.id)}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </PopoverContent>
            </Popover>
            
            <Button variant="outline" size="sm" onClick={refetch} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'feedback' && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card className="bg-card border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Total</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-foreground">{feedback.length}</p>
                </CardContent>
              </Card>
              <Card className="bg-card border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-yellow-400">Pending</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-foreground">
                    {feedback.filter(f => f.status === 'pending' || !f.status).length}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-card border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-red-400">Bugs</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-foreground">
                    {feedback.filter(f => f.type === 'bug').length}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-card border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-green-400">Addressed</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-foreground">
                    {feedback.filter(f => f.status === 'addressed').length}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Filters */}
            <Card className="mb-6 bg-card border-border">
              <CardContent className="pt-4">
                <div className="flex flex-wrap gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm text-muted-foreground">Type</label>
                    <Select value={filterType} onValueChange={(v) => setFilterType(v as FeedbackType)}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="bug">Bug</SelectItem>
                        <SelectItem value="feature">Feature</SelectItem>
                        <SelectItem value="general">General</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm text-muted-foreground">Status</label>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="reviewed">Reviewed</SelectItem>
                        <SelectItem value="addressed">Addressed</SelectItem>
                        <SelectItem value="dismissed">Dismissed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Feedback Table */}
            <Card className="bg-card border-border">
              <CardContent className="p-0">
                {error ? (
                  <div className="p-8 text-center">
                    <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
                    <p className="text-destructive">{error}</p>
                  </div>
                ) : loading ? (
                  <div className="p-4 space-y-4">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="h-16 w-full" />
                    ))}
                  </div>
                ) : feedback.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    No feedback found
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border hover:bg-muted/50">
                          <TableHead 
                            className="w-[100px] cursor-pointer"
                            onClick={() => toggleSort('type')}
                          >
                            <div className="flex items-center gap-1">
                              Type
                              <SortIcon field="type" />
                            </div>
                          </TableHead>
                          <TableHead>Message</TableHead>
                          <TableHead className="w-[140px]">User</TableHead>
                          <TableHead 
                            className="w-[130px] cursor-pointer"
                            onClick={() => toggleSort('created_at')}
                          >
                            <div className="flex items-center gap-1">
                              Date
                              <SortIcon field="created_at" />
                            </div>
                          </TableHead>
                          <TableHead 
                            className="w-[140px] cursor-pointer"
                            onClick={() => toggleSort('status')}
                          >
                            <div className="flex items-center gap-1">
                              Status
                              <SortIcon field="status" />
                            </div>
                          </TableHead>
                          <TableHead className="w-[80px]">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {feedback.map((item) => (
                          <TableRow key={item.id} className="border-border hover:bg-muted/50">
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {typeIcons[item.type] || typeIcons.general}
                                <span className="text-sm capitalize text-foreground">{item.type}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <button
                                onClick={() => setSelectedFeedback(item)}
                                className="text-left hover:bg-muted/50 p-1 -m-1 rounded transition-colors"
                              >
                                <p className="text-sm text-foreground line-clamp-2 max-w-md">
                                  {item.message}
                                </p>
                                {item.message.length > 100 && (
                                  <span className="text-xs text-primary hover:underline">View full message</span>
                                )}
                              </button>
                            </TableCell>
                            <TableCell>
                              <p className="text-sm text-muted-foreground truncate max-w-[120px]">
                                {item.user_name || item.user_email || 'Anonymous'}
                              </p>
                            </TableCell>
                            <TableCell>
                              <p className="text-sm text-muted-foreground">
                                {format(new Date(item.created_at), 'MMM d, yyyy')}
                              </p>
                            </TableCell>
                            <TableCell>
                              <Select 
                                value={item.status || 'pending'} 
                                onValueChange={(v) => handleStatusChange(item.id, v as FeedbackStatus)}
                              >
                                <SelectTrigger className="h-8 w-[120px]">
                                  <Badge 
                                    variant="outline" 
                                    className={statusColors[item.status || 'pending']}
                                  >
                                    {item.status || 'pending'}
                                  </Badge>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="reviewed">Reviewed</SelectItem>
                                  <SelectItem value="addressed">Addressed</SelectItem>
                                  <SelectItem value="dismissed">Dismissed</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => setSelectedFeedback(item)}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Delete Feedback?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        This action cannot be undone. This will permanently delete this feedback entry.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => handleDelete(item.id)}
                                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                      >
                                        Delete
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {activeTab === 'users' && (
          <>
            {/* Users Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card className="bg-card border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary" />
                    Total Users
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-foreground">
                    {usersLoading ? <Skeleton className="h-8 w-16" /> : totalUsers}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-card border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-yellow-400 flex items-center gap-2">
                    <Crown className="h-4 w-4" />
                    Premium Users
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-foreground">
                    {usersLoading ? <Skeleton className="h-8 w-16" /> : users.filter(u => u.is_premium).length}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-card border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-green-400 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Verified Emails
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-foreground">
                    {usersLoading ? <Skeleton className="h-8 w-16" /> : users.filter(u => u.email_confirmed).length}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-card border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-purple-400 flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Founders
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-foreground">
                    {usersLoading ? <Skeleton className="h-8 w-16" /> : users.filter(u => u.is_evolver_founder).length}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Search + Create Test User */}
            <Card className="mb-6 bg-card border-border">
              <CardContent className="pt-4">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name, display name, or referral code..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleUserSearch()}
                      className="pl-10"
                    />
                  </div>
                  <Button onClick={handleUserSearch} disabled={usersLoading}>
                    <Search className="h-4 w-4 mr-2" />
                    Search
                  </Button>
                  <Button variant="outline" onClick={() => fetchUsers(1, '')} disabled={usersLoading}>
                    <RefreshCw className={`h-4 w-4 ${usersLoading ? 'animate-spin' : ''}`} />
                  </Button>
                  <Button variant="outline" onClick={handleExportCsv} disabled={usersLoading}>
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                  <Button variant="default" onClick={() => setShowCreateTestUser(true)}>
                    <Users className="h-4 w-4 mr-2" />
                    Create Test User
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Users Table */}
            <Card className="bg-card border-border">
              <CardContent className="pt-4">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name, display name, or referral code..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleUserSearch()}
                      className="pl-10"
                    />
                  </div>
                  <Button onClick={handleUserSearch} disabled={usersLoading}>
                    <Search className="h-4 w-4 mr-2" />
                    Search
                  </Button>
                  <Button variant="outline" onClick={() => fetchUsers(1, '')} disabled={usersLoading}>
                    <RefreshCw className={`h-4 w-4 ${usersLoading ? 'animate-spin' : ''}`} />
                  </Button>
                  <Button variant="outline" onClick={handleExportCsv} disabled={usersLoading}>
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Users Table */}
            <Card className="bg-card border-border">
              <CardContent className="p-0">
                {usersError ? (
                  <div className="p-8 text-center">
                    <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
                    <p className="text-destructive">{usersError}</p>
                  </div>
                ) : usersLoading ? (
                  <div className="p-4 space-y-4">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="h-16 w-full" />
                    ))}
                  </div>
                ) : users.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    No users found
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-border hover:bg-muted/50">
                            <TableHead className="w-[50px]"></TableHead>
                            <TableHead>User</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead className="w-[100px]">Status</TableHead>
                            <TableHead className="w-[100px]">Tier</TableHead>
                            <TableHead className="w-[70px]">Workouts</TableHead>
                            <TableHead className="w-[120px]">Joined</TableHead>
                            <TableHead className="w-[120px]">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {users.map((user) => (
                            <TableRow key={user.id} className="border-border hover:bg-muted/50">
                              <TableCell>
                                <Avatar className="h-8 w-8">
                                  <AvatarImage src={user.avatar_url || undefined} />
                                  <AvatarFallback className="text-xs">
                                    {(user.name || user.display_name || 'U').charAt(0).toUpperCase()}
                                  </AvatarFallback>
                                </Avatar>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col">
                                  <span className="font-medium text-foreground">
                                    {user.name || user.display_name || 'Unknown'}
                                  </span>
                                  <span className="text-xs text-muted-foreground font-mono">
                                    {user.id.slice(0, 8)}...
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1.5">
                                  <span className="text-sm text-muted-foreground truncate max-w-[180px]">
                                    {user.email || 'No email'}
                                  </span>
                                  {user.email_confirmed ? (
                                    <CheckCircle className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                                  ) : (
                                    <AlertCircle className="h-3.5 w-3.5 text-yellow-500 flex-shrink-0" />
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-wrap gap-1">
                                  {user.is_premium && (
                                    <Badge variant="outline" className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-xs">
                                      <Crown className="h-3 w-3 mr-1" />
                                      Premium
                                    </Badge>
                                  )}
                                  {user.is_evolver_founder && (
                                    <Badge variant="outline" className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-xs">
                                      Founder
                                    </Badge>
                                  )}
                                  {!user.is_premium && !user.is_evolver_founder && (
                                    <Badge variant="outline" className="text-xs">Free</Badge>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <span className="text-sm text-muted-foreground capitalize">
                                  {user.current_tier || 'Seed'}
                                </span>
                              </TableCell>
                              <TableCell>
                                <span className="text-sm font-medium">
                                  {user.total_workouts || 0}
                                </span>
                              </TableCell>
                              <TableCell>
                                <span className="text-sm text-muted-foreground">
                                  {user.created_at ? format(new Date(user.created_at), 'MMM d, yyyy') : '-'}
                                </span>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => handleViewActivity(user)}
                                    title="View Activity"
                                  >
                                    <Activity className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setSelectedUser(user)}
                                    className={user.is_premium ? 'border-yellow-500/30 hover:bg-yellow-500/10' : ''}
                                  >
                                    <Crown className={`h-3.5 w-3.5 mr-1 ${user.is_premium ? 'text-yellow-400' : ''}`} />
                                    {user.is_premium ? 'Manage' : 'Grant'}
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between p-4 border-t border-border">
                        <p className="text-sm text-muted-foreground">
                          Page {currentPage} of {totalPages} ({totalUsers} total users)
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage <= 1 || usersLoading}
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage >= totalPages || usersLoading}
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {activeTab === 'ai-usage' && (
          <>
            {/* AI Usage Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              <Card className="bg-card border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Zap className="h-4 w-4 text-primary" />
                    Total Requests
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-foreground">
                    {aiLoading ? <Skeleton className="h-8 w-16" /> : aiTotalRequests.toLocaleString()}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-card border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Brain className="h-4 w-4 text-purple-400" />
                    Total Tokens (Est.)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-foreground">
                    {aiLoading ? <Skeleton className="h-8 w-24" /> : aiTotalTokens.toLocaleString()}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-card border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Active Users</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-foreground">
                    {aiLoading ? <Skeleton className="h-8 w-12" /> : aiTopUsers.length}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Top Users Table */}
            <Card className="bg-card border-border">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Top AI Users by Token Usage</CardTitle>
                  <Button variant="outline" size="sm" onClick={aiRefetch} disabled={aiLoading}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${aiLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {aiError ? (
                  <div className="p-8 text-center">
                    <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
                    <p className="text-destructive">{aiError}</p>
                  </div>
                ) : aiLoading ? (
                  <div className="p-4 space-y-4">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : aiTopUsers.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    No AI usage data yet
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border hover:bg-muted/50">
                          <TableHead className="w-[60px]">#</TableHead>
                          <TableHead>User</TableHead>
                          <TableHead className="text-right">Requests</TableHead>
                          <TableHead className="text-right">Tokens (Est.)</TableHead>
                          <TableHead className="text-right">Avg Tokens/Req</TableHead>
                          <TableHead>Last Request</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {aiTopUsers.map((user, index) => (
                          <TableRow key={user.user_id} className="border-border hover:bg-muted/50">
                            <TableCell>
                              <Badge variant="outline" className={
                                index === 0 ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' :
                                index === 1 ? 'bg-gray-400/20 text-gray-300 border-gray-400/30' :
                                index === 2 ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' :
                                'bg-muted text-muted-foreground'
                              }>
                                {index + 1}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col">
                                <span className="text-sm font-medium text-foreground">
                                  {user.user_name || 'Unknown User'}
                                </span>
                                <span className="text-xs text-muted-foreground font-mono">
                                  {user.user_id.slice(0, 8)}...
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {user.total_requests.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right font-medium text-primary">
                              {user.total_tokens.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right text-muted-foreground">
                              {Math.round(user.total_tokens / user.total_requests).toLocaleString()}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {formatDistanceToNow(new Date(user.last_request), { addSuffix: true })}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {activeTab === 'push' && (
          <AdminPushPanel />
        )}

        {activeTab === 'premium-audit' && (
          <>
            {/* Premium Audit Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              <Card className="bg-card border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-primary" />
                    Total Changes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-foreground">
                    {premiumLogsLoading ? <Skeleton className="h-8 w-16" /> : premiumLogs.length}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-card border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-green-400 flex items-center gap-2">
                    Granted
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-foreground">
                    {premiumLogsLoading ? <Skeleton className="h-8 w-16" /> : premiumLogs.filter(l => l.action === 'premium_granted').length}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-card border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-red-400 flex items-center gap-2">
                    Revoked
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-foreground">
                    {premiumLogsLoading ? <Skeleton className="h-8 w-16" /> : premiumLogs.filter(l => l.action === 'premium_revoked').length}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Premium Audit Log Table */}
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Premium Status Changes</CardTitle>
                <Button variant="outline" size="sm" onClick={premiumLogsRefetch} disabled={premiumLogsLoading}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${premiumLogsLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                {premiumLogsError ? (
                  <div className="p-8 text-center">
                    <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
                    <p className="text-destructive">{premiumLogsError}</p>
                  </div>
                ) : premiumLogsLoading ? (
                  <div className="p-4 space-y-4">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : premiumLogs.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    No premium status changes recorded
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border hover:bg-muted/50">
                          <TableHead className="w-[140px]">Date</TableHead>
                          <TableHead className="w-[120px]">Action</TableHead>
                          <TableHead className="w-[200px]">User ID</TableHead>
                          <TableHead className="w-[120px]">Triggered By</TableHead>
                          <TableHead className="w-[120px]">Source</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {premiumLogs.map((log) => (
                          <TableRow key={log.id} className="border-border hover:bg-muted/50">
                            <TableCell className="text-sm text-muted-foreground">
                              {format(new Date(log.created_at), 'MMM d, h:mm a')}
                            </TableCell>
                            <TableCell>
                              <Badge 
                                variant="outline" 
                                className={log.action === 'premium_granted' 
                                  ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                                  : 'bg-red-500/20 text-red-400 border-red-500/30'
                                }
                              >
                                {log.action === 'premium_granted' ? 'Granted' : 'Revoked'}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono text-xs text-muted-foreground">
                              {log.user_id.slice(0, 8)}...
                            </TableCell>
                            <TableCell>
                              <Badge 
                                variant="outline" 
                                className={log.triggered_by === 'service_role' 
                                  ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' 
                                  : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                                }
                              >
                                {log.triggered_by === 'service_role' ? 'System' : 'Blocked'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {log.source || '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Debug Tab - Platform Detection */}
        {activeTab === 'debug' && (
          <PlatformDebugPanel />
        )}
      </div>

      {/* Feedback Detail Modal */}
      <Dialog open={!!selectedFeedback} onOpenChange={() => setSelectedFeedback(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedFeedback && typeIcons[selectedFeedback.type]}
              <span className="capitalize">{selectedFeedback?.type} Feedback</span>
            </DialogTitle>
            <DialogDescription>
              Submitted {selectedFeedback && formatDistanceToNow(new Date(selectedFeedback.created_at), { addSuffix: true })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-1">From</h4>
              <p className="text-sm text-foreground">
                {selectedFeedback?.user_name || 'Anonymous'}
                {selectedFeedback?.user_email && (
                  <span className="text-muted-foreground ml-2">({selectedFeedback.user_email})</span>
                )}
              </p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-1">Message</h4>
              <p className="text-sm text-foreground whitespace-pre-wrap bg-muted/50 p-3 rounded-lg">
                {selectedFeedback?.message}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-1">Status</h4>
                <Badge 
                  variant="outline" 
                  className={statusColors[selectedFeedback?.status || 'pending']}
                >
                  {selectedFeedback?.status || 'pending'}
                </Badge>
              </div>
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-1">Date</h4>
                <p className="text-sm text-foreground">
                  {selectedFeedback && format(new Date(selectedFeedback.created_at), 'PPpp')}
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedFeedback(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Premium Toggle Modal */}
      <Dialog open={!!selectedUser} onOpenChange={() => { setSelectedUser(null); setPremiumReason(''); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className={selectedUser?.is_premium ? 'text-yellow-400' : ''} />
              {selectedUser?.is_premium ? 'Revoke Premium' : 'Grant Premium'}
            </DialogTitle>
            <DialogDescription>
              {selectedUser?.is_premium 
                ? `Remove premium access from ${selectedUser?.name || selectedUser?.email || 'this user'}`
                : `Grant premium access to ${selectedUser?.name || selectedUser?.email || 'this user'}`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-1">User</h4>
              <p className="text-sm text-foreground">{selectedUser?.name || 'Unknown'}</p>
              <p className="text-xs text-muted-foreground">{selectedUser?.email}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-1">Reason (optional)</h4>
              <Textarea
                placeholder="Enter reason for this action..."
                value={premiumReason}
                onChange={(e) => setPremiumReason(e.target.value)}
                rows={2}
              />
            </div>
            {selectedUser?.is_premium && (
              <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                <input
                  type="checkbox"
                  id="cancelStripe"
                  checked={cancelStripeSubscription}
                  onChange={(e) => setCancelStripeSubscription(e.target.checked)}
                  className="h-4 w-4 rounded border-destructive/50 text-destructive focus:ring-destructive"
                />
                <label htmlFor="cancelStripe" className="text-sm text-destructive flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Also cancel Stripe subscription (if any)
                </label>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setSelectedUser(null); setPremiumReason(''); setCancelStripeSubscription(true); }}>
              Cancel
            </Button>
            <Button 
              onClick={() => selectedUser && handleTogglePremium(selectedUser)}
              disabled={isToggling}
              variant={selectedUser?.is_premium ? 'destructive' : 'default'}
            >
              {isToggling ? 'Processing...' : (selectedUser?.is_premium ? 'Revoke Premium' : 'Grant Premium')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Activity Modal */}
      <Dialog open={!!viewingActivityUser} onOpenChange={() => handleCloseActivity()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" />
              User Activity - {viewingActivityUser?.name || viewingActivityUser?.email || 'User'}
            </DialogTitle>
            <DialogDescription>
              View workout sessions, chat history, and nutrition logs
            </DialogDescription>
          </DialogHeader>

          {activityLoading ? (
            <div className="flex items-center justify-center p-8">
              <RefreshCw className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : activityError ? (
            <div className="p-8 text-center">
              <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
              <p className="text-destructive">{activityError}</p>
            </div>
          ) : activity ? (
            <>
              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <Dumbbell className="h-5 w-5 mx-auto mb-1 text-primary" />
                  <p className="text-xl font-bold">{activity.summary.totalWorkouts}</p>
                  <p className="text-xs text-muted-foreground">Workouts</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <MessageSquare className="h-5 w-5 mx-auto mb-1 text-blue-400" />
                  <p className="text-xl font-bold">{activity.summary.userMessages}</p>
                  <p className="text-xs text-muted-foreground">Messages</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <Apple className="h-5 w-5 mx-auto mb-1 text-green-400" />
                  <p className="text-xl font-bold">{activity.summary.totalNutritionLogs}</p>
                  <p className="text-xs text-muted-foreground">Meals Logged</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <Award className="h-5 w-5 mx-auto mb-1 text-yellow-400" />
                  <p className="text-xl font-bold">{activity.summary.totalBadges}</p>
                  <p className="text-xs text-muted-foreground">Badges</p>
                </div>
              </div>

              {/* Activity Tabs */}
              <Tabs value={activityTab} onValueChange={setActivityTab} className="flex-1 overflow-hidden flex flex-col">
                <TabsList className="w-full justify-start">
                  <TabsTrigger value="workouts" className="flex items-center gap-1.5">
                    <Dumbbell className="h-4 w-4" />
                    Workouts ({activity.workouts.length})
                  </TabsTrigger>
                  <TabsTrigger value="messages" className="flex items-center gap-1.5">
                    <MessageSquare className="h-4 w-4" />
                    Messages ({activity.messages.length})
                  </TabsTrigger>
                  <TabsTrigger value="nutrition" className="flex items-center gap-1.5">
                    <Apple className="h-4 w-4" />
                    Nutrition ({activity.nutritionLogs.length})
                  </TabsTrigger>
                  <TabsTrigger value="weight" className="flex items-center gap-1.5">
                    <Scale className="h-4 w-4" />
                    Weight ({activity.weightHistory.length})
                  </TabsTrigger>
                </TabsList>

                <ScrollArea className="flex-1 mt-4">
                  {activityTab === 'workouts' && (
                    <div className="space-y-2">
                      {activity.workouts.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">No workouts found</p>
                      ) : (
                        activity.workouts.map((workout) => (
                          <div key={workout.id} className="bg-muted/30 rounded-lg p-3 flex items-center justify-between">
                            <div>
                              <p className="font-medium">{workout.workout_type || 'Workout'}</p>
                              <p className="text-sm text-muted-foreground">
                                {workout.exercises_completed || 0} exercises • {workout.duration_minutes || 0} min
                                {workout.calories_burned ? ` • ${workout.calories_burned} cal` : ''}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-muted-foreground">
                                {format(new Date(workout.completed_at), 'MMM d, yyyy')}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {format(new Date(workout.completed_at), 'h:mm a')}
                              </p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {activityTab === 'messages' && (
                    <div className="space-y-2">
                      {activity.messages.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">No messages found</p>
                      ) : (
                        activity.messages.filter(m => !m.is_ai).slice(0, 50).map((message) => (
                          <div key={message.id} className="bg-muted/30 rounded-lg p-3">
                            <p className="text-sm line-clamp-3">{message.text}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}
                            </p>
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {activityTab === 'nutrition' && (
                    <div className="space-y-2">
                      {activity.nutritionLogs.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">No nutrition logs found</p>
                      ) : (
                        activity.nutritionLogs.map((log) => (
                          <div key={log.id} className="bg-muted/30 rounded-lg p-3 flex items-center justify-between">
                            <div>
                              <p className="font-medium">{log.meal_type || 'Meal'}</p>
                              <p className="text-sm text-muted-foreground">
                                {log.calories} cal • {log.protein_g || 0}g P • {log.carbs_g || 0}g C • {log.fat_g || 0}g F
                              </p>
                              {log.notes && (
                                <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{log.notes}</p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-muted-foreground">
                                {format(new Date(log.logged_date), 'MMM d, yyyy')}
                              </p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {activityTab === 'weight' && (
                    <div className="space-y-2">
                      {activity.weightHistory.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">No weight history found</p>
                      ) : (
                        activity.weightHistory.map((entry) => (
                          <div key={entry.id} className="bg-muted/30 rounded-lg p-3 flex items-center justify-between">
                            <div>
                              <p className="font-medium">{entry.weight} lbs</p>
                              {entry.notes && (
                                <p className="text-xs text-muted-foreground">{entry.notes}</p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-muted-foreground">
                                {format(new Date(entry.logged_at), 'MMM d, yyyy')}
                              </p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </ScrollArea>
              </Tabs>
            </>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseActivity}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Test User Modal */}
      <Dialog open={showCreateTestUser} onOpenChange={setShowCreateTestUser}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Test User</DialogTitle>
            <DialogDescription>
              Create a test account with full premium features, sample workouts, and nutrition data.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Email</label>
              <Input
                type="email"
                placeholder="test@example.com"
                value={testUserEmail}
                onChange={(e) => setTestUserEmail(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Password</label>
              <Input
                type="password"
                placeholder="Enter password"
                value={testUserPassword}
                onChange={(e) => setTestUserPassword(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Name (optional)</label>
              <Input
                placeholder="Test User"
                value={testUserName}
                onChange={(e) => setTestUserName(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateTestUser(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateTestUser} disabled={isCreatingTestUser}>
              {isCreatingTestUser ? 'Creating...' : 'Create Test User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
